import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { updateUserProfile } from '../../actions/userProfileActions';
import media from '../../constants/media';
import theme from '../../constants/theme';
import { getNextLocale, getQueryString } from '../../helpers/url/urlHelpers';
import IPageViewModel from '../../interfaces/umbracoNodes/pageViewModel';
import { RootState } from '../../store';

const LanguageSwitch = styled.a`
	display: inline-flex;
	font-size: 18px;
	line-height: 1.4;
	color: ${theme.colorsV2.mainDark};
	transition: all 200ms ease;
	text-decoration: none;
	text-transform: uppercase;
	background-color: transparent;

	&:active {
		color: ${theme.colorsV2.lightRed};
	}

	${media.desktopSmall`
    	cursor: pointer;
		&:hover {
			color: ${theme.colorsV2.mainRed};
		}
	`};
`;

interface Props {
	className?: string;
}

const LocaleSwitcher: FC<Props> = ({ className }) => {
	const pageReducer = useSelector((store: RootState) => store.pageReducer);
	const userProfileReducer = useSelector((store: RootState) => store.userProfileReducer);
	const { page }: { page: IPageViewModel } = pageReducer;
	const { isLoggedIn, userProfile } = userProfileReducer;
	const dispatch = useDispatch<any>();
	const query = getQueryString();
	const nextLocale = getNextLocale();

	const userLanguage = isLoggedIn ? userProfile.language ?? 'IS' : '';
	const nextCulturePath = page.CulturePaths.find(x => x.culture === (nextLocale === 'is' ? 'is' : 'en-us'));
	const nextPath = nextCulturePath
		? `${nextCulturePath.path}${query ? `?${query}` : ''}`
		: `/${nextLocale === 'is' ? '' : 'en'}`;

	const switchUserLocale = () => {
		const newUserProfile = userProfile.clone();
		newUserProfile.language = nextLocale.toUpperCase();
		dispatch(updateUserProfile(newUserProfile));
	};

	useEffect(() => {
		if (!!isLoggedIn && userLanguage === nextLocale.toUpperCase()) {
			window.location.replace(nextPath);
		}
	}, [isLoggedIn, userLanguage, nextLocale, nextPath]);

	return (
		<>
			{!!isLoggedIn && (
				<LanguageSwitch as="button" onClick={switchUserLocale} lang={nextLocale} className={className}>
					{nextLocale}
				</LanguageSwitch>
			)}
			{!isLoggedIn && (
				<LanguageSwitch href={nextPath} lang={nextLocale} hreflang={nextLocale} className={className}>
					{nextLocale}
				</LanguageSwitch>
			)}
		</>
	);
};

export default LocaleSwitcher;
